import React from "react";
import { Form, Formik } from "formik";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import {
  GetAllBranches,
  GetAllLMSCourses,
  GetTeam,
} from "../../../../../Services";
import {
  CustomAsyncAutocomplete,
  CustomCard,
  ReskinningInputs,
  ReskinningRadioGroup,
} from "../../../../../Components";
import { useTranslate } from "../../../../../Hooks";

import useStyles from "./styles";

function EditSchemaSettingsForm({ schemaDetails, formRef }) {
  const styles = useStyles();
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const initialValues = {
    isOverrideLeadCapacitySettingEnabled:
      schemaDetails.isOverrideLeadCapacitySettingEnabled,
    rotationSchemeLeadDistributionType:
      schemaDetails.rotationSchemeLeadDistributionType,
    sla: schemaDetails.sla,
    rotationSchemeBranches: schemaDetails.rotationSchemeBranches,
    rotationSchemeTeams: schemaDetails.rotationSchemeTeams,
    rotationSchemeCourses: schemaDetails.rotationSchemeCourses,
  };

  return (
    <Formik initialValues={initialValues} innerRef={formRef}>
      {({ values, setFieldValue, handleChange }) => (
        <Form>
          <Box className={styles.contentWrapper}>
            <CustomCard
              boxShadow="xs"
              borderRadius="xl"
              borderColor="secondary"
              classes={styles.settingsForm}
            >
              <Box
                display="flex"
                alignItems={"center"}
                className={styles.fieldWrapper}
              >
                <span className={styles.radioGroupLabel}>
                  {translate("lead-distribution-strategy")}
                </span>
                <ReskinningRadioGroup
                  name="rotationSchemeLeadDistributionType"
                  rootCustomClasses={styles.leadDistribution}
                  data={[
                    {
                      value: "3",
                      label: translate("dist-strategy-all-system"),
                    },
                    {
                      value: "1",
                      label: translate("dist-strategy-current-schema-assigned"),
                    },
                    {
                      value: "2",
                      label: translate("dist-strategy-current-schema-active"),
                    },
                    {
                      value: "4",
                      label: translate("dist-strategy-one-by-one"),
                    },
                  ]}
                  idRef="Actions"
                  onSelectedRadioChanged={(e) => {
                    setFieldValue(
                      "rotationSchemeLeadDistributionType",
                      e.target.value
                    );
                  }}
                  value={String(values.rotationSchemeLeadDistributionType)}
                  labelInput="label"
                  valueInput="value"
                />
              </Box>
              <Box
                display="flex"
                alignItems={"center"}
                className={styles.fieldWrapper}
              >
                <span className={styles.radioGroupLabel}>
                  {translate("agent-load-limit-rule")}
                </span>
                <ReskinningRadioGroup
                  rootCustomClasses={styles.radioRootWrapper}
                  data={[
                    {
                      value: "false",
                      label: translate("agent-load-all-system"),
                    },
                    {
                      value: "true",
                      label: translate("agent-load-current-schema"),
                    },
                  ]}
                  idRef="Actions"
                  onSelectedRadioChanged={(e) => {
                    setFieldValue(
                      "isOverrideLeadCapacitySettingEnabled",
                      e.target.value
                    );
                  }}
                  value={String(values.isOverrideLeadCapacitySettingEnabled)}
                  name="Active"
                  labelInput="label"
                  valueInput="value"
                  themeClass="theme-line"
                />
              </Box>
              <Box
                display="flex"
                alignItems={"center"}
                className={styles.fieldWrapper}
              >
                <span
                  className={clsx(
                    styles.radioGroupLabel,
                    styles.alignSelfStart
                  )}
                >
                  {translate("restrict-to-branches")}
                </span>
                <Box className={styles.fieldContainer}>
                  <CustomAsyncAutocomplete
                    rootCustomClasses={styles.inputwrapper}
                    autocompleteClasses={clsx({
                      [styles.disabled]:
                        values.rotationSchemeTeams?.length ||
                        values.rotationSchemeCourses?.length,
                    })}
                    placeholder={translate("branch-input-placeholder")}
                    onFetchData={GetAllBranches}
                    chipsLabel={(option) => option.branchName || ""}
                    displayLabel={(option) => option.branchName || ""}
                    getOptionSelected={(option, value) =>
                      option?.branchId === value?.branchId
                    }
                    onChange={(_e, value) => {
                      setFieldValue("rotationSchemeBranches", value);
                    }}
                    selectedValues={values.rotationSchemeBranches}
                    mapper={(data) => data}
                    disabled={
                      values.rotationSchemeTeams?.length ||
                      values.rotationSchemeCourses?.length
                    }
                    filter={{
                      pageSize: 25,
                      pageIndex: 1,
                      filterBy: null,
                      orderBy: null,
                    }}
                    multiple
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems={"center"}
                className={styles.fieldWrapper}
              >
                <span
                  className={clsx(
                    styles.radioGroupLabel,
                    styles.alignSelfStart
                  )}
                >
                  {translate("restrict-to-teams")}
                </span>
                <Box className={styles.fieldContainer}>
                  <CustomAsyncAutocomplete
                    rootCustomClasses={styles.inputwrapper}
                    placeholder={translate("teams-input-placeholder")}
                    autocompleteClasses={clsx({
                      [styles.disabled]:
                        values.rotationSchemeBranches.length ||
                        values.rotationSchemeCourses?.length,
                    })}
                    disabled={
                      values.rotationSchemeBranches.length ||
                      values.rotationSchemeCourses?.length
                    }
                    onFetchData={(filter) => GetTeam(...Object.values(filter))}
                    chipsLabel={(option) =>
                      option.teamsName || option.teamName || ""
                    }
                    onChange={(_e, value) => {
                      setFieldValue("rotationSchemeTeams", value);
                    }}
                    selectedValues={values.rotationSchemeTeams}
                    displayLabel={(option) =>
                      option.teamsName || option.teamName || ""
                    }
                    getOptionSelected={(option, value) =>
                      value.teamsId === option.teamsId ||
                      value.teamId === option.teamId
                    }
                    mapper={(data) => data}
                    filter={{
                      pageIndex: 1,
                      pageSize: 25,
                      search: "",
                    }}
                    multiple
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems={"center"}
                className={styles.fieldWrapper}
              >
                <span
                  className={clsx(
                    styles.radioGroupLabel,
                    styles.alignSelfStart
                  )}
                >
                  {translate("agent-response-time")}
                </span>
                <Box className={styles.fieldContainer}>
                  <ReskinningInputs
                    name="sla"
                    idRef="sla"
                    fieldClasses={styles.inputwrapper}
                    value={values.sla}
                    type="number"
                    onInputChanged={handleChange}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems={"center"}
                className={styles.fieldWrapper}
              >
                <span
                  className={clsx(
                    styles.radioGroupLabel,
                    styles.alignSelfStart
                  )}
                >
                  {translate("courses-label")}
                </span>
                <Box className={styles.fieldContainer}>
                  <CustomAsyncAutocomplete
                    rootCustomClasses={styles.inputwrapper}
                    placeholder={"select-courses"}
                    autocompleteClasses={clsx({
                      [styles.disabled]:
                        values.rotationSchemeBranches.length ||
                        values.rotationSchemeTeams?.length,
                    })}
                    disabled={
                      values.rotationSchemeBranches.length ||
                      values.rotationSchemeTeams?.length
                    }
                    onFetchData={GetAllLMSCourses}
                    onChange={(_e, value) => {
                      setFieldValue("rotationSchemeCourses", value);
                    }}
                    selectedValues={values.rotationSchemeCourses}
                    filter={{
                      pageIndex: 1,
                      pageSize: 100,
                      search: "",
                    }}
                    multiple
                    chipsLabel={(option) => option?.lmsCourseName}
                    displayLabel={(option) => option?.lmsCourseName}
                    mapper={(data) => data}
                  />
                </Box>
              </Box>
              {values.rotationSchemeCourses.map((course, index) => (
                <Box
                  display="flex"
                  marginTop={"24px"}
                  className={styles.scoreWrapper}
                  key={course.lmsCourseId}
                >
                  <span
                    className={clsx(
                      styles.radioGroupLabel,
                      styles.alignSelfStart
                    )}
                  >
                    {course.lmsCourseName}
                  </span>
                  <Box display={"flex"} className={styles.scoreContainer}>
                    <label className={styles.scoreLabel}>
                      {translate("score-min")}
                    </label>
                    <ReskinningInputs
                      type="number"
                      value={course.scoreFrom || 0}
                      onInputChanged={(e) => {
                        const courses = [...values.rotationSchemeCourses];
                        courses[index] = {
                          ...courses[index],
                          scoreFrom: e.target.value,
                        };
                        setFieldValue("rotationSchemeCourses", courses);
                      }}
                      fieldClasses={styles.inputwrapper}
                      min={0}
                      defaultValue={0}
                    />
                    <label className={styles.scoreLabel}>
                      {translate("score-max")}
                    </label>
                    <ReskinningInputs
                      value={course.scoreTo || 100}
                      onInputChanged={(e) => {
                        const courses = [...values.rotationSchemeCourses];
                        courses[index] = {
                          ...courses[index],
                          scoreTo: e.target.value,
                        };
                        setFieldValue("rotationSchemeCourses", courses);
                      }}
                      fieldClasses={styles.inputwrapper}
                      max={100}
                      type="number"
                      defaultValue={100}
                    />
                  </Box>
                </Box>
              ))}
            </CustomCard>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default EditSchemaSettingsForm;
